import { useRef, useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  hasAtLeastRole,
  IMPORT_STATUS_LABELS,
  IMPORT_TYPE_LABELS,
  IMPORT_TYPES,
  peutDeposerImport,
  isBackgroundImport,
  type ImportBatch,
  type ImportType,
} from '@kn/shared';
import { api } from '../../api/client';
import { messageErreur } from '../../api/error-message';
import { useAuth } from '../../auth/auth-context';

const IMPORTS_QUERY_KEY = ['imports'] as const;

/** Periode de rafraichissement tant qu'un lot est en cours (pas de WebSocket). */
const INTERVALLE_POLLING = 2000;

function etiquetteStatut(batch: ImportBatch): string {
  if (batch.status === 'DONE') return 'etiquette etiquette--actif';
  if (batch.status === 'FAILED') return 'etiquette etiquette--inactif';
  if (batch.status === 'AWAITING_CONFIRMATION') return 'etiquette etiquette--attente';
  return 'etiquette';
}

/** Un lot suspendu par le garde-fou de rattrapage attend un arbitrage humain. */
function formaterNombre(valeur: string): string {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return valeur;
  return nombre.toLocaleString('fr-FR', { maximumFractionDigits: 3 });
}

function attendConfirmation(batch: ImportBatch): boolean {
  return batch.status === 'AWAITING_CONFIRMATION';
}

/**
 * Lignes intégrées d'un lot : créations **et** mises à jour (N24). Une fiche
 * article réimportée ne crée rien et met tout à jour ; n'afficher que les
 * créations donnait « 0 intégrée » pour 452 articles rafraîchis.
 */
function integrees(batch: ImportBatch): number {
  return batch.rowsInserted + batch.rowsUpdated;
}

function formaterJournee(iso: string): string {
  return iso.slice(0, 10).split('-').reverse().join('/');
}

function estEnCours(batch: ImportBatch): boolean {
  return batch.status === 'QUEUED' || batch.status === 'RUNNING';
}

export function ImportsPage(): ReactElement {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const peutImporter = user ? hasAtLeastRole(user.role, 'OPERATEUR') : false;
  // Les types reserves a l'ADMIN (decision de Sam du 28/10) ne sont pas
  // proposes : la regle est la meme qu'a l'API, lue sur le schema partage.
  const typesProposes = user ? IMPORT_TYPES.filter((t) => peutDeposerImport(user.role, t)) : [];

  const champFichier = useRef<HTMLInputElement>(null);
  const [type, setType] = useState<ImportType>('FICHE_ARTICLE');
  const [dateInventaire, setDateInventaire] = useState('2026-01-01');
  const [feuille, setFeuille] = useState('');
  const [colonnes, setColonnes] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const listeQuery = useQuery({
    queryKey: IMPORTS_QUERY_KEY,
    queryFn: () => api.listImports({ pageSize: 100 }),
    // Un lot en file ou en cours : on suit sa progression par polling.
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some(estEnCours) ? INTERVALLE_POLLING : false,
  });

  const confirmation = useMutation({
    mutationFn: (id: string) => api.confirmerRattrapage(id),
    onSuccess: () => {
      setErreur(null);
      void queryClient.invalidateQueries({ queryKey: IMPORTS_QUERY_KEY });
    },
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const depot = useMutation({
    mutationFn: (fichier: File) =>
      api.uploadImport(type, fichier, {
        ...(type === 'STOCK_INITIAL' ? { dateInventaire } : {}),
        ...(feuille.trim() ? { feuille: feuille.trim() } : {}),
        ...(colonnes.trim() ? { colonnes: colonnes.trim() } : {}),
      }),
    onSuccess: (batch) => {
      setErreur(null);
      setDetailId(batch.id);
      if (champFichier.current) {
        champFichier.current.value = '';
      }
      void queryClient.invalidateQueries({ queryKey: IMPORTS_QUERY_KEY });
    },
    onError: (error: unknown) => {
      setDetailId(null);
      setErreur(messageErreur(error));
      void queryClient.invalidateQueries({ queryKey: IMPORTS_QUERY_KEY });
    },
  });

  function handleDepot(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setErreur(null);
    setDetailId(null);

    const fichier = champFichier.current?.files?.[0];
    if (!fichier) {
      setErreur('Selectionnez un fichier .xlsx ou .xlsm.');
      return;
    }
    depot.mutate(fichier);
  }

  const lots = listeQuery.data?.items ?? [];
  const detail = lots.find((batch) => batch.id === detailId) ?? null;
  const enFile = lots.filter(estEnCours);

  return (
    <>
      <h1>Imports</h1>
      <p className="sous-titre">
        Dépôt des référentiels et des quatre sources de mouvements. Un fichier déjà importe est
        refuse : l&apos;empreinte SHA-256 garantit qu&apos;aucun doublon n&apos;est créé. Les
        sources volumineuses sont traitées en arriere-plan, une à la fois ; l&apos;application reste
        utilisable pendant le traitement.
      </p>

      {erreur && (
        <p className="alerte alerte--erreur" role="alert">
          {erreur}
        </p>
      )}

      {enFile.length > 0 && (
        <p className="alerte alerte--info">
          {enFile.length} lot(s) en attente ou en cours de traitement. Cette page se rafraichit
          automatiquement.
        </p>
      )}

      {peutImporter ? (
        <div className="carte">
          <h2>Nouveau dépôt</h2>
          <form onSubmit={handleDepot} noValidate>
            <div className="barre-actions">
              <label className="champ" htmlFor="import-type">
                <span>Type de fichier</span>
                <select
                  id="import-type"
                  value={type}
                  onChange={(event) => setType(event.target.value as ImportType)}
                >
                  {typesProposes.map((valeur) => (
                    <option key={valeur} value={valeur}>
                      {IMPORT_TYPE_LABELS[valeur]}
                    </option>
                  ))}
                </select>
              </label>

              <label className="champ" htmlFor="import-fichier">
                <span>Fichier (.xlsx / .xlsm)</span>
                <input id="import-fichier" type="file" accept=".xlsx,.xlsm" ref={champFichier} />
              </label>

              {type === 'STOCK_INITIAL' && (
                <label className="champ" htmlFor="import-date">
                  <span>Date d&apos;inventaire</span>
                  <input
                    id="import-date"
                    type="date"
                    value={dateInventaire}
                    onChange={(event) => setDateInventaire(event.target.value)}
                  />
                </label>
              )}

              <label className="champ" htmlFor="import-feuille">
                <span>Feuille (facultatif)</span>
                <input
                  id="import-feuille"
                  type="text"
                  placeholder="Nom de l onglet"
                  value={feuille}
                  onChange={(event) => setFeuille(event.target.value)}
                />
              </label>

              <button type="submit" className="bouton" disabled={depot.isPending}>
                {depot.isPending ? 'Dépôt en cours...' : 'Importer'}
              </button>
            </div>

            <label className="champ" htmlFor="import-colonnes">
              <span>Mapping de colonnes (facultatif, JSON)</span>
              <input
                id="import-colonnes"
                type="text"
                placeholder='{"codpro":"Ref interne","uvc":"Stock physique"}'
                value={colonnes}
                onChange={(event) => setColonnes(event.target.value)}
              />
            </label>
            <p className="sous-titre">
              Les exports Generix sont livres dans un classeur a plusieurs onglets : sans précision,
              l&apos;onglet est reconnu automatiquement d&apos;après le type choisi.
              {isBackgroundImport(type) &&
                ' Ce type est traité en arriere-plan : la reponse est immediate, le résultat arrive dans la liste ci-dessous.'}
            </p>
          </form>
        </div>
      ) : (
        <p className="alerte alerte--info">
          Votre rôle ne permet pas de déposer un fichier. La consultation des lots reste ouverte.
        </p>
      )}

      {detail && (
        <RapportLot
          batch={detail}
          peutConfirmer={peutImporter}
          confirmationEnCours={confirmation.isPending}
          onConfirmer={() => confirmation.mutate(detail.id)}
        />
      )}

      <div className="carte">
        <h2>Historique des lots</h2>

        {listeQuery.isPending && <p className="chargement">Chargement des lots...</p>}
        {listeQuery.isError && (
          <p className="alerte alerte--erreur">{messageErreur(listeQuery.error)}</p>
        )}

        {listeQuery.data && listeQuery.data.total === 0 && (
          <p className="alerte alerte--info">Aucun import realise à ce jour.</p>
        )}

        {listeQuery.data && listeQuery.data.total > 0 && (
          <table className="tableau">
            <caption className="sous-titre">{listeQuery.data.total} lot(s)</caption>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Type</th>
                <th scope="col">Fichier</th>
                <th scope="col">Feuille</th>
                <th scope="col">Statut</th>
                <th scope="col">Lues</th>
                <th scope="col">Intégrées</th>
                <th scope="col">Ignorées</th>
                <th scope="col">Hors 2026</th>
                <th scope="col">Rejetées</th>
                <th scope="col">Journées</th>
                <th scope="col">Par</th>
                <th scope="col">Rapport</th>
              </tr>
            </thead>
            <tbody>
              {lots.map((batch) => (
                <tr key={batch.id}>
                  <td>{new Date(batch.createdAt).toLocaleString('fr-FR')}</td>
                  <td>{IMPORT_TYPE_LABELS[batch.type]}</td>
                  <td>{batch.filename}</td>
                  <td>{batch.sheetName ?? '—'}</td>
                  <td>
                    <span className={etiquetteStatut(batch)}>
                      {IMPORT_STATUS_LABELS[batch.status]}
                    </span>
                    {estEnCours(batch) && (
                      // Le total de lignes du classeur n'est connu qu'a la fin
                      // de la lecture : la barre reste indeterminee, et c'est le
                      // compteur de lignes lues qui porte la progression reelle.
                      <div
                        className="progression"
                        role="progressbar"
                        aria-valuetext={
                          batch.status === 'QUEUED'
                            ? "En attente d'un poste de traitement"
                            : `${batch.progressRows.toLocaleString('fr-FR')} lignes lues`
                        }
                      >
                        <div className="progression__barre" />
                      </div>
                    )}
                  </td>
                  <td>
                    {estEnCours(batch)
                      ? batch.progressRows.toLocaleString('fr-FR')
                      : batch.rowsRead.toLocaleString('fr-FR')}
                  </td>
                  <td>{integrees(batch).toLocaleString('fr-FR')}</td>
                  <td>{batch.rowsIgnored.toLocaleString('fr-FR')}</td>
                  <td>{batch.rowsOutOfScope.toLocaleString('fr-FR')}</td>
                  <td>{batch.rowsRejected.toLocaleString('fr-FR')}</td>
                  <td title="Journées intégrées / ignorées / remplacées">
                    {batch.daysInserted} / {batch.daysIgnored} / {batch.daysReplaced}
                  </td>
                  <td>{batch.userEmail ?? '—'}</td>
                  <td>
                    <button
                      type="button"
                      className="bouton bouton--lien"
                      onClick={() => setDetailId(batch.id)}
                    >
                      Consulter
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/** Decisions d'idempotence par journee (regle 2). */
const LIBELLES_DECISION: Record<'INTEGREE' | 'IGNOREE' | 'REMPLACEE', string> = {
  INTEGREE: 'Intégrée (journée inconnue)',
  IGNOREE: 'Ignorée (contenu identique)',
  REMPLACEE: 'Remplacée (contenu différent)',
};

function RapportLot({
  batch,
  peutConfirmer,
  confirmationEnCours,
  onConfirmer,
}: {
  batch: ImportBatch;
  peutConfirmer: boolean;
  confirmationEnCours: boolean;
  onConfirmer: () => void;
}): ReactElement {
  const rapport = batch.report;

  return (
    <div className="carte">
      <h2>
        Rapport du lot &mdash; {batch.filename} ({IMPORT_STATUS_LABELS[batch.status]})
      </h2>
      <p className="sous-titre">
        {batch.rowsRead.toLocaleString('fr-FR')} ligne(s) lue(s) &middot;{' '}
        {integrees(batch).toLocaleString('fr-FR')} intégrée(s), dont{' '}
        {batch.rowsInserted.toLocaleString('fr-FR')} créée(s) et{' '}
        {batch.rowsUpdated.toLocaleString('fr-FR')} mise(s) à jour &middot;{' '}
        {batch.rowsIgnored.toLocaleString('fr-FR')} ignorée(s) &middot;{' '}
        {batch.rowsOutOfScope.toLocaleString('fr-FR')} hors 2026 &middot;{' '}
        {batch.rowsRejected.toLocaleString('fr-FR')} rejetée(s)
      </p>
      <p className="sous-titre">
        Journées&nbsp;: {batch.daysInserted} intégrée(s), {batch.daysIgnored} identique(s) donc
        ignorée(s), {batch.daysReplaced} remplacée(s).
      </p>

      {rapport?.erreur && (
        <p className="alerte alerte--erreur">Échec du lot&nbsp;: {rapport.erreur}</p>
      )}

      {rapport?.avertissements && rapport.avertissements.length > 0 && (
        <ul>
          {rapport.avertissements.map((avertissement) => (
            <li key={avertissement}>{avertissement}</li>
          ))}
        </ul>
      )}

      {attendConfirmation(batch) && rapport?.journeesProtegees && (
        <>
          <h3>Rattrapage suspendu &mdash; journées plus récentes en base</h3>
          <p className="alerte alerte--erreur">
            Ce fichier remplacerait {rapport.journeesProtegees.length} journée(s) écrite(s) par une
            extraction plus récente. <strong>Aucune donnée n&apos;a été modifiée.</strong> Confirmez
            seulement si ce fichier fait foi sur ces journées.
          </p>
          <ul className="liste-cles">
            {rapport.journeesProtegees.map((journee) => (
              <li key={journee}>{journee}</li>
            ))}
          </ul>
          {peutConfirmer ? (
            <button
              type="button"
              className="bouton bouton--danger"
              onClick={onConfirmer}
              disabled={confirmationEnCours}
            >
              {confirmationEnCours ? 'Confirmation…' : 'Confirmer le rattrapage'}
            </button>
          ) : (
            <p className="sous-titre">Seul un opérateur peut confirmer un rattrapage.</p>
          )}
        </>
      )}

      {attendConfirmation(batch) && rapport?.ancrageProtege && (
        <>
          <h3>Ré-ancrage suspendu &mdash; un inventaire est déjà en place</h3>
          <p className="alerte alerte--erreur">
            L&apos;inventaire est le <strong>point zéro</strong> de tout le rapprochement. Ce
            fichier le déplacerait. <strong>Aucune donnée n&apos;a été modifiée.</strong> Confirmez
            seulement s&apos;il s&apos;agit d&apos;un nouvel inventaire physique.
          </p>
          <table className="tableau">
            <thead>
              <tr>
                <th scope="col">Ancrage</th>
                <th scope="col">Date</th>
                <th scope="col">Articles</th>
                <th scope="col">UVC</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">En base</th>
                <td>{rapport.ancrageProtege.dateExistante}</td>
                <td className="droite">
                  {rapport.ancrageProtege.articlesExistants.toLocaleString('fr-FR')}
                </td>
                <td className="droite">{formaterNombre(rapport.ancrageProtege.uvcExistantes)}</td>
              </tr>
              <tr>
                <th scope="row">Proposé par ce fichier</th>
                <td>{rapport.ancrageProtege.dateProposee}</td>
                <td className="droite">
                  {rapport.ancrageProtege.articlesProposes.toLocaleString('fr-FR')}
                </td>
                <td className="droite">{formaterNombre(rapport.ancrageProtege.uvcProposees)}</td>
              </tr>
              <tr>
                <th scope="row">Écart</th>
                <td>
                  {rapport.ancrageProtege.articlesAjoutes} ajouté(s),{' '}
                  {rapport.ancrageProtege.articlesRetires} retiré(s),{' '}
                  {rapport.ancrageProtege.articlesModifies} modifié(s)
                </td>
                <td className="droite">
                  {(
                    rapport.ancrageProtege.articlesProposes -
                    rapport.ancrageProtege.articlesExistants
                  ).toLocaleString('fr-FR')}
                </td>
                <td className="droite">
                  <strong>{formaterNombre(rapport.ancrageProtege.ecartUvc)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
          {peutConfirmer ? (
            <button
              type="button"
              className="bouton bouton--danger"
              onClick={onConfirmer}
              disabled={confirmationEnCours}
            >
              {confirmationEnCours ? 'Confirmation…' : 'Confirmer le ré-ancrage'}
            </button>
          ) : (
            <p className="sous-titre">Seul un opérateur peut confirmer un ré-ancrage.</p>
          )}
        </>
      )}

      {/*
        Garde-fou *CO/ (N19) : une anomalie distincte de la règle 4a, avec son
        propre message — un lot peut porter les deux.
      */}
      {rapport?.commandesCopacking && (
        <>
          <h3>Extraction non conforme &mdash; commandes copacking *CO/</h3>
          <p className="alerte alerte--erreur">{rapport.commandesCopacking.message}</p>
        </>
      )}

      {/*
        N22 : une journée de la période du fichier, active en base, sans aucune
        ligne dans ce lot. Rien n'a été désactivé : c'est une alerte.
      */}
      {rapport?.journeesAbsentes && (
        <>
          <h3>Journées absentes du fichier</h3>
          <p className="alerte alerte--erreur">{rapport.journeesAbsentes.message}</p>
          <p className="sous-titre">
            Ces journées ont des lignes en base pour cette source, mais aucune dans ce fichier,
            alors qu&apos;il couvre leur période. Elles sont conservées telles quelles.
          </p>
          <table className="tableau">
            <thead>
              <tr>
                <th scope="col">Journée</th>
                <th scope="col">Lignes conservées</th>
                <th scope="col">UVC</th>
              </tr>
            </thead>
            <tbody>
              {rapport.journeesAbsentes.journees.map((j) => (
                <tr key={j.journee}>
                  <td>{formaterJournee(j.journee)}</td>
                  <td className="droite">{j.lignes.toLocaleString('fr-FR')}</td>
                  <td className="droite">{formaterNombre(j.uvc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {rapport?.clesManquantes && rapport.clesManquantes.length > 0 && (
        <>
          <h3>Données manquantes &mdash; clés absentes de cet export</h3>
          <p className="alerte alerte--erreur">
            Ces clés etaient présentes dans une extraction précédente et ne le sont plus. Le lot est
            signale en anomalie : vérifier l&apos;extraction à la source avant de conclure.
          </p>
          <ul className="liste-cles">
            {rapport.clesManquantes.slice(0, 100).map((cle) => (
              <li key={cle}>{cle}</li>
            ))}
          </ul>
          {rapport.clesManquantes.length > 100 && (
            <p className="sous-titre">
              {rapport.clesManquantes.length - 100} autre(s) clé(s) non listee(s).
            </p>
          )}
        </>
      )}

      {rapport?.retraitsAttendus && rapport.retraitsAttendus.length > 0 && (
        <>
          <h3>Retrait attendu (*CO/) &mdash; clés retirées par ce fichier corrigé</h3>
          <p className="alerte alerte--info">
            Ces clés ne portaient que des lignes de commandes copacking *CO/. Leur disparition est
            la correction attendue : elles ne comptent pas comme données manquantes.
          </p>
          <ul className="liste-cles">
            {rapport.retraitsAttendus.slice(0, 100).map((cle) => (
              <li key={cle}>{cle}</li>
            ))}
          </ul>
          {rapport.retraitsAttendus.length > 100 && (
            <p className="sous-titre">
              {rapport.retraitsAttendus.length - 100} autre(s) clé(s) non listée(s).
            </p>
          )}
        </>
      )}

      {rapport?.journees && rapport.journees.length > 0 && (
        <>
          <h3>Journées couvertes par le fichier</h3>
          <p className="sous-titre">
            Une journée identique à celle déjà en base est ignorée ; une journée différente remplace
            l&apos;ancienne, qui reste consultable via son lot d&apos;origine.
          </p>
          <table className="tableau">
            <thead>
              <tr>
                <th scope="col">Journée</th>
                <th scope="col">Décision</th>
                <th scope="col">Lignes</th>
              </tr>
            </thead>
            <tbody>
              {rapport.journees.map((journee) => (
                <tr key={journee.journee}>
                  <td>{journee.journee}</td>
                  <td>{LIBELLES_DECISION[journee.decision]}</td>
                  <td>{journee.lignes.toLocaleString('fr-FR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {rapport?.trousSequence && rapport.trousSequence.length > 0 && (
        <>
          <h3>Trous de séquence NUMCMM</h3>
          <p className="sous-titre">
            Information seule : une séquence non continue n&apos;est pas une anomalie en soi.
          </p>
          <p>{rapport.trousSequence.slice(0, 50).join(', ')}</p>
        </>
      )}

      {rapport?.rejets && rapport.rejets.length > 0 && (
        <>
          <h3>Lignes rejetées</h3>
          {rapport.rejetsTronques && (
            <p className="alerte alerte--info">
              La liste des rejets est tronquee&nbsp;: seules les premières lignes sont detaillees.
            </p>
          )}
          <ListeIssues issues={rapport.rejets} colonneValeur="Valeur" />
        </>
      )}
    </div>
  );
}

function ListeIssues({
  issues,
  colonneValeur,
}: {
  issues: Array<{ ligne: number; motif: string; valeur?: string }>;
  colonneValeur: string;
}): ReactElement {
  return (
    <table className="tableau">
      <thead>
        <tr>
          <th scope="col">Ligne</th>
          <th scope="col">Motif</th>
          <th scope="col">{colonneValeur}</th>
        </tr>
      </thead>
      <tbody>
        {issues.slice(0, 200).map((issue, index) => (
          <tr key={`${issue.ligne}-${issue.valeur ?? index}`}>
            <td>{issue.ligne || '—'}</td>
            <td>{issue.motif}</td>
            <td>{issue.valeur ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
